import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MessageSquare,
  X,
  Send,
  Maximize2,
  Minimize2,
  ChevronLeft,
  Plus,
  History,
  Trash2
} from 'lucide-react';

const WidgetMarkdown = ({ content }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
    p: ({children}) => <p className="my-1">{children}</p>,
    ul: ({children}) => <ul className="my-1 ml-3 list-disc">{children}</ul>,
    ol: ({children}) => <ol className="my-1 ml-3 list-decimal">{children}</ol>,
    li: ({children}) => <li className="my-0.5">{children}</li>,
    strong: ({children}) => <strong className="font-semibold">{children}</strong>,
    table: ({children}) => <div className="overflow-x-auto my-1"><table className="min-w-full border-collapse text-xs">{children}</table></div>,
    thead: ({children}) => <thead className="bg-gray-50">{children}</thead>,
    th: ({children}) => <th className="border border-gray-200 px-1.5 py-1 font-semibold text-left text-gray-600">{children}</th>,
    td: ({children}) => <td className="border border-gray-200 px-1.5 py-1">{children}</td>,
  }}>{content}</ReactMarkdown>
);

export default function GlowyAIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState('chat'); // 'chat' | 'list'
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const [typing, setTyping] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const sessionsReqIdRef = useRef(0);
  const [userId] = useState(() => {
    const existing = localStorage.getItem('glowyAiUserId');
    if (existing) return existing;
    const id = uuidv4();
    localStorage.setItem('glowyAiUserId', id);
    return id;
  });

  // --- Effects ---
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, typing, viewMode, isOpen]);

  useEffect(() => {
    if (isOpen) {
        loadSessions().catch(() => {});
    }
  }, [isOpen]);

  // --- API Helpers ---
  const getApiBaseUrl = () => {
    const isPlatformHost = (typeof window !== 'undefined') && /(^|\.)platform\.glowytics\.com$/i.test(window.location.hostname);
    return isPlatformHost ? 'https://api.glowytics.com' : 'https://api.glowytics.com';
  };

  const getBrandId = () => localStorage.getItem('currentBrandId');

  const loadSessions = async () => {
    const brandId = getBrandId();
    const authToken = localStorage.getItem('token');
    if (!brandId) return;

    try {
      const reqId = ++sessionsReqIdRef.current;
      const url = `${getApiBaseUrl()}/api/ai/conversations`;
      const headers = { 'X-Brand-ID': brandId };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      
      const resp = await fetch(url, { headers, credentials: 'include' });
      if (resp.ok && resp.status !== 204) {
        const data = await resp.json();
        if (reqId === sessionsReqIdRef.current) setSessions(data.conversations || data.sessions || []);
      }
    } catch (e) {
      console.warn('Widget: loadSessions failed', e);
    }
  };

  const loadMessages = async (conversationId) => {
    const brandId = getBrandId();
    const authToken = localStorage.getItem('token');
    if (!brandId || !conversationId) return;

    try {
      const url = `${getApiBaseUrl()}/api/ai/conversations/${conversationId}`;
      const headers = { 'X-Brand-ID': brandId };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      
      const resp = await fetch(url, { headers, credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        const formatted = (data.messages || []).map((m) => ({
          ...m,
          role: (m.sender_role ? m.sender_role.toLowerCase() : m.role || 'assistant')
        }));
        setMessages(formatted);
      }
    } catch (e) {
      console.warn('Widget: loadMessages failed', e);
    }
  };

  const createNewSession = () => {
    setActiveSessionId(null);
    setMessages([]);
    setViewMode('chat');
  };

  const selectSession = async (id) => {
    setActiveSessionId(id);
    setViewMode('chat');
    setMessages([]); 
    await loadMessages(id);
  };

  const handleDeleteSession = async (e, id) => {
    e.stopPropagation();
    const brandId = getBrandId();
    if (!brandId) return;
    try {
        const authToken = localStorage.getItem('token');
        const headers = { 'X-Brand-ID': brandId };
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
        await fetch(`${getApiBaseUrl()}/api/ai/conversations/${id}`, {
            method: 'DELETE',
            headers,
            credentials: 'include'
        });
        if (activeSessionId === id) {
            createNewSession();
        }
        await loadSessions();
    } catch (err) {
        console.error(err);
    }
  };

  // --- Main Logic (The "Engine") ---
  const sendMessage = async () => {
    const text = inputValue.trim();
    if (!text) return;

    const brandId = getBrandId();
    // Try to get brand name from various storages if not in context
    const brandName = localStorage.getItem('currentBrandName') 
        || (() => { try { return JSON.parse(localStorage.getItem('selectedBrand') || '{}').name; } catch { return null; } })();
    
    if (!brandId) {
      setMessages(prev => [...prev, { role: 'system', content: 'No brand selected.' }]);
      return;
    }

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setSending(true);

    try {
      const authToken = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'X-Brand-ID': brandId
      };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      // --- NLU LOGIC COPIED FROM GLOWYAIGAMINI.JS ---
      const lower = text.toLowerCase();
      const wantsChannelMonthly = /(monthly\s*channel\s*revenue|channel\s*monthly|kanal\s*(gelir|ciro).*ayl[ıi]k|ayl[ıi]k\s*kanal\s*(gelir|ciro)|kanal.*ayl[ıi]k.*(gelir|ciro)|monthly\s*breakdown\s*by\s*channel)/i.test(lower);
      const wantsMonthlyTrends = /(monthly\s*performance\s*trends|monthly\s*trend(s)?|ayl[ıi]k\s*performans(\s*trend(leri)?)?|ayl[ıi]k\s*kırılım|monthly\s*breakdown)/i.test(lower);
      const wantsWeekOverWeek = /(week-?over-?week|wow\s*summary|this\s*week\s*vs\s*last\s*week|hafta\s*hafta|bu\s*hafta.*geçen\s*hafta)/i.test(lower);
      const wantsWeekdayRevenue = /(weekday\s*revenue|revenue\s*by\s*weekday|günlere\s*göre\s*gelir|hafta\s*içi.*gelir|gün\s*bazlı\s*gelir)/i.test(lower);
      const wantsCampaignTypesOrNames = /(campaign\s*(types?|tipleri)|kampanya\s*tip(ler|i)|campaign\s*names?|kampanya\s*isim(ler|i)|campaign\s*list|kampanya\s*listesi)/i.test(lower);
      const wantsBudgetAlloc = /(bütçe\s*alokasyon(u)?|budget\s*allocation|bütçeyi\s*(artır|azalt)|bütçe\s*önerisi|budget\s*suggestion)/i.test(lower);
      const wantsActiveCampaigns = /(aktif\s*kampanyalar(ımız)?|aktif\s*kampanyalar(ımız)?\s*nelerdir|aktif\s*kampanyaları\s*listele|kampanyalar(ımız)?\s*nelerdir|kampanya\s*isim(leri|lerini)\s*(listele|nedir|nelerdir)?|tüm\s*kampanyalar)/i.test(lower);
      const mentionsMeta = /(meta\s*ads?|facebook\s*ads?|meta)/i.test(lower);
      const mentionsGoogle = /(google\s*ads?|adwords|search|pmax|display|shopping)/i.test(lower);
      const wantsTopProducts = /(top\s*10\s*(ürün|product)s?|en\s*çok\s*gelir\s*getiren\s*(ürün|product)ler|best\s*selling\s*products)/i.test(lower);
      const wantsProductDaily = /(product\s*performance|ürün\s*performans(ı)?|ürün\s*analitiği|product\s*daily|daily\s*product)/i.test(lower);
      const wantsBrandMonthly = /(brand\s*monthly|monthly\s*brand|marka\s*ayl[ıi]k|ayl[ıi]k\s*marka)/i.test(lower);
      const wantsCategoryMonthly = /(category\s*monthly|monthly\s*category|kategori\s*ayl[ıi]k|ayl[ıi]k\s*kategori)/i.test(lower);
      const wantsBrandWeekly = /(brand\s*weekly|weekly\s*brand|marka\s*haftal[ıi]k|haftal[ıi]k\s*marka)/i.test(lower);
      const wantsCategoryWeekly = /(category\s*weekly|weekly\s*category|kategori\s*haftal[ıi]k|haftal[ıi]k\s*kategori)/i.test(lower);

      const options = { narrative: true };
      if (wantsChannelMonthly) { options.includeChannelMonthlyRevenue = true; options.channelMonthlyDateRange = '180'; }
      if (wantsMonthlyTrends) { options.includeMonthlyPerformanceTrends = true; options.monthlyTrendsDateRange = '365'; }
      if (wantsWeekOverWeek) { options.includeWeekCompare = true; }
      if (wantsWeekdayRevenue) { options.breakdown = 'daily'; }
      if (wantsCampaignTypesOrNames || wantsActiveCampaigns || wantsBudgetAlloc) {
        options.includeCampaignTypes = true;
        options.includeCampaignNames = true;
        options.campaignInventoryDateRange = '180';
        if (mentionsMeta && !mentionsGoogle) options.campaignInventoryPlatform = 'meta';
        if (mentionsGoogle && !mentionsMeta) options.campaignInventoryPlatform = 'google';
      }
      if (wantsTopProducts) { options.includeTop10Products = true; options.productTop10DateRange = '30'; }
      if (wantsProductDaily) { options.includeDailyProductPerformance = true; }
      if (wantsBrandMonthly) { options.includeMonthlyItemBrandSummary = true; options.itemBrandMonthlyDateRange = 'last12'; }
      if (wantsCategoryMonthly) { options.includeMonthlyCategorySummary = true; options.categoryMonthlyDateRange = 'last12'; }
      if (wantsBrandWeekly) { options.includeWeeklyItemBrandSummary = true; options.itemBrandWeeklyDateRange = 'last12'; }
      if (wantsCategoryWeekly) { options.includeWeeklyCategoryPerformance = true; options.categoryWeeklyDateRange = 'last12'; }

      const payload = {
        conversationId: activeSessionId,
        message: text,
        options,
        userId,
        brandName
      };
      
      const intentHints = [];
      if (wantsChannelMonthly) intentHints.push('channel_monthly_revenue');
      if (wantsMonthlyTrends) intentHints.push('monthly_performance_trends');
      if (wantsWeekOverWeek) intentHints.push('weekly');
      if (wantsWeekdayRevenue) intentHints.push('daily_breakdown');
      if (wantsCampaignTypesOrNames || wantsActiveCampaigns || wantsBudgetAlloc) intentHints.push('campaign_inventory');
      if (wantsTopProducts) intentHints.push('product_top10');
      if (wantsProductDaily) intentHints.push('product_daily');
      if (wantsBrandMonthly) intentHints.push('brand_monthly');
      if (wantsCategoryMonthly) intentHints.push('category_monthly');
      if (wantsBrandWeekly) intentHints.push('brand_weekly');
      if (wantsCategoryWeekly) intentHints.push('category_weekly');
      if (intentHints.length) payload.intentHints = intentHints;

      const resp = await fetch(`${getApiBaseUrl()}/api/ai/gemini-chat/v1/respond`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!resp.ok) throw new Error(await resp.text());

      const data = await resp.json();

      if (data.conversationId && !activeSessionId) {
        setActiveSessionId(data.conversationId);
      }

      // Handle structured responses
      let extras = {};
      if (data?.structured?.kpiCard) {
        extras.kpiCard = data.structured.kpiCard;
      } 
        else if (data?.structured?.campaign_inventory_perf) {
        extras.campaignPerfCard = data.structured.campaign_inventory_perf;
      }
      else if (data?.structured?.category_top_recent) {
        extras.categoryCard = {
            title: 'Top categories (30d)',
            rows: data.structured.category_top_recent.rows,
            period: data.structured.category_top_recent.period
        };
      }
      else if (typeof data?.assistantMessage === 'string') {
         if (data.assistantMessage.startsWith('__KPI_CARD_V1__')) {
             try { extras.kpiCard = JSON.parse(data.assistantMessage.replace('__KPI_CARD_V1__', '')); } catch {}
         }
         else if (data.assistantMessage.startsWith('__KPI_CARD_TOP10_PRODUCTS_V1__')) {
             try { extras.top10Card = JSON.parse(data.assistantMessage.replace('__KPI_CARD_TOP10_PRODUCTS_V1__', '')); } catch {}
         }
        }

      await appendAssistantTypewriter(data.assistantMessage || 'No response', extras);
      await loadSessions();

    } catch (e) {
      setMessages(prev => [...prev, { role: 'system', content: `Error: ${e.message}` }]);
    } finally {
      setSending(false);
    }
  };

  const appendAssistantTypewriter = (fullText, extras = {}) => new Promise((resolve) => {
    let cleanText = fullText
        .replace('__KPI_CARD_V1__', '')
        .replace('__KPI_CARD_CAMPAIGN_V1__', '')
        .replace('__KPI_CARD_TOP10_PRODUCTS_V1__', '');
        
    if (!cleanText.trim() && Object.keys(extras).length > 0) {
        cleanText = "Here is the data you requested:";
    } else if (cleanText.trim().startsWith('{')) {
        cleanText = "I've analyzed the data:";
    }

    setTyping(true);
    let targetIndex = -1;
    setMessages((prev) => {
      targetIndex = prev.length;
      return [...prev, { role: 'assistant', content: '', ...extras }];
    });
    
    let i = 0;
    const interval = setInterval(() => {
      i += 3;
      const slice = cleanText.slice(0, i);
      setMessages((prev) => prev.map((m, idx) => (idx === targetIndex ? { ...m, content: slice } : m)));
      if (i >= cleanText.length) {
        clearInterval(interval);
        setTyping(false);
        resolve();
      }
    }, 10);
  });

  // --- Render Helpers ---
  const renderKpiCard = (card) => {
    if (!card) return null;
        return (
        <div className="text-xs bg-white rounded-lg p-2 border border-gray-200 mt-2 shadow-sm overflow-hidden">
            <div className="font-semibold text-gray-700 mb-2 border-b pb-1">{card.title}</div>
            <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                    <div className="font-medium text-gray-500 text-[10px]">Google Rev</div>
                    <div>₺{Number(card.google?.revenue||0).toLocaleString()}</div>
                    </div>
                <div className="space-y-1">
                    <div className="font-medium text-gray-500 text-[10px]">Meta Rev</div>
                    <div>₺{Number(card.meta?.revenue||0).toLocaleString()}</div>
                        </div>
                <div className="space-y-1">
                    <div className="font-medium text-gray-500 text-[10px]">GA4 Rev</div>
                    <div>₺{Number(card.ga4?.revenue||0).toLocaleString()}</div>
                </div>
                <div className="space-y-1">
                    <div className="font-medium text-gray-500 text-[10px]">GA4 ROAS</div>
                    <div>{(() => {
                        const cost = (Number(card.google?.cost||0) + Number(card.meta?.cost||0));
                        const rev = Number(card.ga4?.revenue||0);
                        return cost > 0 ? (rev/cost).toFixed(2)+'x' : '-';
                    })()}</div>
                </div>
            </div>
            </div>
        );
  };

  const renderTableCard = (title, rows, cols) => {
      if (!rows || !rows.length) return null;
        return (
        <div className="text-xs bg-white rounded-lg p-2 border border-gray-200 mt-2 shadow-sm">
            <div className="font-semibold text-gray-700 mb-2 border-b pb-1">{title}</div>
            <div className="overflow-x-auto">
                <table className="w-full text-left whitespace-nowrap">
                    <thead>
                        <tr className="text-gray-500 border-b">
                            {cols.map(c => <th key={c.key} className="pb-1 pr-2 font-medium">{c.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.slice(0,8).map((r, i) => (
                            <tr key={i} className="border-b border-gray-50 last:border-0">
                                {cols.map(c => (
                                    <td key={c.key} className="py-1 pr-2">
                                        {c.format ? c.format(r[c.key], r) : r[c.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            </div>
        );
  };

  if (!isOpen) {
  return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 group outline-none focus:outline-none"
        title="Ask GlowyAI"
      >
        <div className="relative flex items-center justify-center transition-transform duration-300 group-hover:scale-105 active:scale-95">
            {/* Soft Glow on Hover */}
            <div className="absolute -inset-2 bg-violet-500/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Main Icon Container with Gradient Border */}
            <div className="relative h-16 w-16 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.15)] bg-gradient-to-tr from-violet-600 via-fuchsia-500 to-indigo-600 p-[2px] flex items-center justify-center overflow-hidden transition-all duration-300 group-hover:shadow-[0_4px_25px_rgba(124,58,237,0.4)]">
                <div className="h-full w-full bg-white rounded-full overflow-hidden">
                    <img src="/GlowyAIv3.png" alt="AI" className="h-full w-full object-cover" />
                </div>
            </div>

            {/* Status Dot */}
            <div className="absolute bottom-0.5 right-0.5 h-3.5 w-3.5 bg-emerald-500 border-2 border-white rounded-full shadow-sm"></div>
        </div>
                </button>
    );
  }

  return (
    <div className={`fixed z-50 flex flex-col bg-white shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 font-sans
        ${isExpanded 
            ? 'top-4 bottom-4 right-4 left-4 md:left-auto md:w-[600px] rounded-2xl' 
            : 'bottom-6 right-6 w-[90vw] md:w-[400px] h-[600px] max-h-[85vh] rounded-2xl'
        }
    `}>
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 p-3 flex items-center justify-between text-white shrink-0">
        <div className="flex items-center gap-2">
            {viewMode === 'chat' && activeSessionId && (
                <button onClick={() => setViewMode('list')} className="p-1 hover:bg-white/20 rounded-md mr-1">
                    <ChevronLeft className="w-5 h-5" />
                </button>
            )}
            {viewMode === 'list' && (
                <History className="w-5 h-5 opacity-80" />
            )}
            <div>
                <h3 className="font-bold text-sm tracking-wide">GlowyAI</h3>
                <p className="text-[10px] opacity-80">Marketing Assistant</p>
            </div>
          </div>
        <div className="flex items-center gap-1">
            <button 
                onClick={() => isExpanded ? setIsExpanded(false) : setIsExpanded(true)} 
                className="p-1.5 hover:bg-white/20 rounded-md hidden md:block"
                title={isExpanded ? "Minimize" : "Expand"}
            >
                {isExpanded ? <Minimize2 className="w-4 h-4"/> : <Maximize2 className="w-4 h-4"/>}
            </button>
            <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-white/20 rounded-md">
                <X className="w-5 h-5"/>
            </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden relative bg-gray-50 flex flex-col">
        
        {viewMode === 'list' ? (
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
                <button 
                    onClick={createNewSession}
                    className="w-full py-3 px-4 bg-white border border-dashed border-gray-300 rounded-xl text-blue-600 text-sm font-medium hover:bg-blue-50 transition flex items-center justify-center gap-2"
                >
                    <Plus className="w-4 h-4" /> New Chat
                </button>
                {sessions.length === 0 && <div className="text-center text-gray-400 text-sm py-8">No history yet.</div>}
                {sessions.map(s => (
                    <div key={s.id} onClick={() => selectSession(s.id)} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm cursor-pointer hover:border-violet-200 group relative">
                        <div className="font-medium text-gray-800 text-sm truncate pr-6">{s.title || 'New Conversation'}</div>
                        <div className="text-xs text-gray-400 mt-1 truncate">{s.preview || '...'}</div>
                        <button 
                            onClick={(e) => handleDeleteSession(e, s.id)}
                            className="absolute top-2 right-2 p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded hidden group-hover:block"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ))}
            </div>
        ) : (
            <>
                {/* Chat Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
                    {messages.length === 0 && !activeSessionId && (
                        <div className="text-center mt-10">
                            <div className="w-16 h-16 bg-white rounded-full mx-auto mb-3 shadow-md flex items-center justify-center">
                                <img src="/GlowyAIv3.png" alt="AI" className="w-14 h-14 rounded-full" />
                            </div>
                            <p className="text-sm text-gray-600 font-medium">How can I help you analyze your data today?</p>
                            <div className="mt-4 flex flex-wrap gap-2 justify-center">
                                {['Last 7 days', 'Top products', 'Google vs Meta'].map(tag => (
                                    <button key={tag} onClick={() => { setInputValue(tag); setTimeout(() => sendMessage(), 0); }} className="text-xs bg-white border border-gray-200 px-2 py-1 rounded-full text-gray-600 hover:bg-violet-50 hover:text-violet-600 transition">
                                        {tag}
                            </button>
                        ))}
                    </div>
                </div>
                    )}
                    
                    {messages.map((m, idx) => {
                        const isUser = m.role === 'user';
                        return (
                            <div key={idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                                    isUser ? 'bg-violet-600 text-white rounded-br-none' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                                }`}>
                                    <div className="leading-relaxed">{isUser ? m.content : <WidgetMarkdown content={m.content} />}</div>
                                    
                                    {/* Render Structured Cards if present */}
                                    {!isUser && (
                                        <div className="mt-1">
                                            {m.kpiCard && renderKpiCard(m.kpiCard)}
                                            
                                            {m.categoryCard && renderTableCard(m.categoryCard.title, m.categoryCard.rows, [
                                                { key: 'category', label: 'Cat' },
                                                { key: 'revenue', label: 'Rev', format: (v) => `₺${Number(v||0).toLocaleString()}` },
                                                { key: 'purchases', label: 'Pur', format: (v) => Number(v||0) }
                                            ])}

                                            {m.top10Card && renderTableCard('Top 10 Products', m.top10Card.rows, [
                                                { key: 'name', label: 'Product' },
                                                { key: 'revenue', label: 'Rev', format: (v) => `₺${Number(v||0).toLocaleString()}` }
                                            ])}

                                            {m.campaignPerfCard && (() => {
                                                const card = m.campaignPerfCard;
                                                const allRows = [...(card.google||[]), ...(card.meta||[])].sort((a,b) => b.cost - a.cost);
                                                return renderTableCard('Campaign Perf (Last 9d)', allRows, [
                                                    { key: 'name', label: 'Name' },
                                                    { key: 'cost', label: 'Cost', format: (v) => `₺${Math.round(v)}` },
                                                    { key: 'roas', label: 'ROAS', format: (v, r) => (r.cost > 0 ? (r.revenue/r.cost).toFixed(2)+'x' : '-') }
                                                ]);
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {sending && (
                        <div className="flex justify-start">
                            <div className="bg-white border border-gray-200 px-3 py-2 rounded-2xl rounded-bl-none shadow-sm">
                                <div className="flex gap-1">
                                    <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce"></span>
                                    <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce delay-75"></span>
                                    <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce delay-150"></span>
                                </div>
                            </div>
                </div>
            )}
          </div>

          {/* Input Area */}
                <div className="p-3 bg-white border-t border-gray-200 shrink-0">
                    <form 
                        onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                        className="flex gap-2 items-end"
                    >
                        {activeSessionId && (
                            <button 
                                type="button"
                                onClick={() => setViewMode('list')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                                title="History"
                            >
                                <History className="w-5 h-5" />
                            </button>
                        )}
                        <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                            placeholder="Ask anything..."
                            className="flex-1 bg-gray-100 border-0 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-violet-500 focus:bg-white resize-none max-h-24 scrollbar-hide focus:outline-none text-gray-800"
                            rows={1}
                    disabled={sending}
                />
                <button 
                    type="submit" 
                            disabled={sending || !inputValue.trim()}
                            className="p-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all"
                >
                            <Send className="w-4 h-4" />
                </button>
            </form>
            </div>
            </>
        )}
      </div>
    </div>
  );
}
